import { vpnApi } from '../api/vpn';

export interface FriendConnection {
  sessionId: string;
  hostName: string;
  config: string;
}

/**
 * One-tap connect to a friend by their user id — no manually-typed IDs. (#782)
 *
 * Requests a VPN session against the friend (as host) and fetches its WireGuard
 * config. The browser can't run WireGuard itself, so the caller hands the
 * returned config to the user (download / import into a WireGuard client).
 */
export async function connectToFriend(hostUserId: string): Promise<FriendConnection> {
  const { data: connection } = await vpnApi.requestConnection({ host_user_id: hostUserId });
  if (!connection?.session_id) {
    throw new Error('Failed to establish a connection session.');
  }
  const { data: cfg } = await vpnApi.getSessionConfig(connection.session_id);
  if (!cfg?.config) {
    throw new Error('Failed to retrieve the WireGuard configuration.');
  }
  return {
    sessionId: connection.session_id,
    hostName: connection.host_user_display_name || 'Friend',
    config: cfg.config,
  };
}

/** Sanitize a host name into a safe WireGuard `.conf` file name. */
export function wireGuardConfigFileName(hostName: string): string {
  const base = (hostName || 'connect-share').replace(/[^a-zA-Z0-9-_]+/g, '-').replace(/^-+|-+$/g, '');
  return `${base || 'connect-share'}-wireguard.conf`;
}

/** Trigger a browser download of a WireGuard `.conf` file. */
export function downloadWireGuardConfig(config: string, fileName: string): void {
  if (!config) {
    throw new Error('Cannot download an empty WireGuard configuration.');
  }
  const blob = new Blob([config], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
